import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import styles from '../SignIn/SignIn.module.scss';
import fpStyles from './ForgotPassword.module.scss';

interface Props {
  email: string;
  otp: string[];
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  otpValue: string;
  otpError: boolean;
  otpVerifyLoading: boolean;
  otpVerifyError: Error | null;
  timer: number;
  formatTime: (seconds: number) => string;
  handleInputChange: (value: string, index: number) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, index: number) => void;
  handlePaste: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  handleFocus: (index: number) => void;
  handleOTPVerification: (event: React.FormEvent<HTMLFormElement>) => void;
  handleResendOTP: () => void;
  resendError?: string | null;
}

export const OTPVerificationForm: React.FC<Props> = ({
  email,
  otp,
  inputRefs,
  otpValue,
  otpError,
  otpVerifyLoading,
  otpVerifyError,
  timer,
  formatTime,
  handleInputChange,
  handleKeyDown,
  handlePaste,
  handleFocus,
  handleOTPVerification,
  handleResendOTP,
  resendError,
}) => {
  const { t } = useTranslation('account');

  return (
    <>
      <h1 className={styles.cardTitle}>{t('reset.verify_otp')}</h1>
      <p className={styles.cardSubtitle}>
        {t('reset.otp_sent_to', { email }).split(email).map((part, i, arr) => (
          <React.Fragment key={i}>
            {part}
            {i !== arr.length - 1 && <span style={{ color: 'var(--primary-btn-color, #F59E0B)' }}>{email}</span>}
          </React.Fragment>
        ))}
        <br />
        {t('reset.enter_the_otp')}
      </p>
      <form onSubmit={handleOTPVerification} noValidate>
        <div style={{ textAlign: 'left', width: '100%', marginBottom: '8px' }}>
          <label htmlFor={`otp-input-0`} className={styles.fieldLabel}>
            OTP<span className={styles.required} aria-hidden>*</span>
          </label>
        </div>
        <div className={fpStyles.otpContainer}>
          {otp.map((value, index) => (
            <input
              id={`otp-input-${index}`}
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              maxLength={1}
              value={value}
              onChange={(e) => handleInputChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              onFocus={() => handleFocus(index)}
              className={fpStyles.otpInput}
              placeholder="-"
            />
          ))}
        </div>

        <p className={styles.helperError} role="alert" style={{ visibility: otpError ? 'visible' : 'hidden' }}>
          {otpError ? t('reset.invalid_otp') : '\u00A0'}
        </p>

        <div className={fpStyles.timerContainer}>
          {timer > 0 ? (
            <span>
              {t('reset.resend_code')} <span className={fpStyles.whiteBold}>({timer}s)</span>
            </span>
          ) : (
            <span className={fpStyles.resendLink} onClick={handleResendOTP}>
              {t('reset.resend_code')}
            </span>
          )}
        </div>

        <Button type="submit" color="primary" fullWidth size="large" disabled={otpValue.length < 4 || otpVerifyLoading} label={t('reset.verify_button')} />
      </form>
    </>
  );
};
